import React, { useContext, useEffect, useRef, useState } from "react";
import { Card, Container, DetalheEntrega, Divider, IconBadge, Options } from "./styles";
import LoaderAsync from "../LoaderAsync";
import api from "../../services/api";
import Radio from "../Radio";
import { formatPrice } from "../../util/format";
import { DeliverySubtype, DeliveryType } from "../../util/deliveryType";
import StoreCard from "../StoreCard";
import SelectedAddress from "../SelectedAddress";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { getReceiptTime } from "../../util/friendlyDate";
import { Storefront, LocalShipping, Speed } from '@material-ui/icons';
import { AlertContext } from "../../contexts/AlertContext";
import { track } from "../../analytics";

export default function DeliveryOptionPicker({ customerAddressId, cart, store, mainOptionDelivery, deliverySubtype, setMainOptionDelivery, showOverlay, showPickupModal, responsibleWithdrawal, deliveryMultiplicationFactor, mainAddress, loadingAddresses, showSelectedAddressModal, deliveryUnavailable = false, deliveryUnavailableMessage, canRegisterAddress = false, onDeliveryCoverageUnavailableChange = () => {}, freeShippingBase, onFreteGratisChange }) {
  const { showAlert } = useContext(AlertContext);
  const [loading, setLoading] = useState(false);
  const [storeForDelivery, setStoreForDelivery] = useState(null);
  const [deliveryCoverageUnavailable, setDeliveryCoverageUnavailable] = useState(false);
  const [regraFreteGratis, setRegraFreteGratis] = useState(null);

  useEffect(() => {
    const lojaId = storeForDelivery?.id;
    if (!lojaId) return undefined;
    let ativo = true;
    api
      .get("/stores/free-shipping-rules", { params: { lojaId } })
      .then((response) => {
        if (ativo) setRegraFreteGratis(response.data);
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [storeForDelivery?.id]);

  const valorMinimoFreteGratis = regraFreteGratis?.ativo
    ? parseFloat(regraFreteGratis.valorMinimo) || 0
    : 0;
  const freteGratisNormal =
    !!regraFreteGratis?.ativo &&
    (Number(freeShippingBase) || 0) >= valorMinimoFreteGratis;
  const freteGratisExpressa =
    freteGratisNormal && !!regraFreteGratis.incluiExpressa;

  useEffect(() => {
    if (onFreteGratisChange) {
      onFreteGratisChange({
        normal: freteGratisNormal,
        expressa: freteGratisExpressa,
        valorMinimo: valorMinimoFreteGratis,
        ativo: !!regraFreteGratis?.ativo,
      });
    }
  }, [freteGratisNormal, freteGratisExpressa, valorMinimoFreteGratis, regraFreteGratis, onFreteGratisChange]);
  const lastDeliveryOptionsRequestKey = useRef(null);
  const lastDeliveryOptionsContextKey = useRef(null);
  const cartProductsKey = (cart?.data?.itens || [])
    .map(item => `${item.id}:${item.quantidade}`)
    .sort()
    .join("|");
  // Só o frete manual substitui o cálculo (o calculado do admin é apenas referência).
  const assistedSaleFreightValue = cart?.data?.valorFreteInformado;
  const hasAssistedSaleConfiguredFreight = cart?.data?.vendaAssistida === true &&
    assistedSaleFreightValue !== null &&
    assistedSaleFreightValue !== undefined;
  const assistedSaleFreightKey = `${cart?.data?.carrinhoId || ""}:${hasAssistedSaleConfiguredFreight}:${assistedSaleFreightValue ?? ""}`;
  // A loja da venda assistida só é imposta no endereço combinado. Em outro endereço ela pode
  // não atender, e forçá-la deixava o cliente sem entrega em vez de buscar outra farmácia.
  const assistedSaleStoreId = cart?.data?.vendaAssistida && cart?.data?.lojaId &&
    (!cart?.data?.enderecoId || Number(cart.data.enderecoId) === Number(customerAddressId))
    ? cart.data.lojaId
    : null;
  const selectedStoreId = assistedSaleStoreId || store?.id || "";

  useEffect(() => {
    if (deliveryUnavailable) {
      lastDeliveryOptionsRequestKey.current = null;
      lastDeliveryOptionsContextKey.current = null;
      setStoreForDelivery(null);
      setDeliveryCoverageUnavailable(false);
      onDeliveryCoverageUnavailableChange(false);
      if (mainOptionDelivery?.tipoEntrega === DeliveryType.RECEBER_EM_CASA)
        setMainOptionDelivery(null);
      return;
    }

    const contextKey = `${customerAddressId || ""}:${selectedStoreId}`;
    const requestKey = `${contextKey}:${cartProductsKey}:${assistedSaleFreightKey}`;
    if (!customerAddressId || !cartProductsKey || lastDeliveryOptionsRequestKey.current === requestKey)
      return;

    const preserveSelection = lastDeliveryOptionsContextKey.current === contextKey;
    lastDeliveryOptionsRequestKey.current = requestKey;
    lastDeliveryOptionsContextKey.current = contextKey;
    getDeliveryOptions(customerAddressId, cart, store, preserveSelection)
  }, [customerAddressId, cartProductsKey, selectedStoreId, deliveryUnavailable, assistedSaleFreightKey])

  async function getDeliveryOptions(customerAddressId, cart, store, preserveSelection) {
    if (customerAddressId && cart) {
      try {
        setLoading(true);
        showOverlay(true);
        clearOptions(preserveSelection);
        setDeliveryCoverageUnavailable(false);
        onDeliveryCoverageUnavailableChange(false);
        const products = getProductsInCart(cart.data.itens);
        const { data } = await api.post(`/stores/customerAddress/${customerAddressId}`, {
          products: products,
          storeIdPrimary: assistedSaleStoreId || store?.id,
          cartId: cart?.data?.carrinhoId
        })
        if (data) {
          setStoreForDelivery({
            ...data
          });
          if (data.freteConfiguradoVendaAssistida)
            setMainOptionDelivery(data);
          else if (preserveSelection && mainOptionDelivery?.tipoEntrega === DeliveryType.RECEBER_EM_CASA)
            setMainOptionDelivery(data, deliverySubtype);
        }
        // Ramo COM cobertura (regra A: consulta_cep XOR cep_sem_cobertura por avaliação).
        track("consulta_cep", { has_coverage: true });
      } catch (e) {
        // Ramo SEM cobertura.
        track("cep_sem_cobertura", { cep_sem_cobertura: true });
        const errorMessage = e.response?.data?.user || "Erro ao obter opções de entrega";
        const errorType = e.response?.data?.type;
        const isDeliveryCoverageUnavailable = [
          "ERROR_STORE_DELIVERY_CITY",
          "ERROR_STORE_NOTFOUND",
          "ERROR_PRODUCT_UNAVAILABLE"
        ].includes(errorType);

        if (isDeliveryCoverageUnavailable) {
          setStoreForDelivery(null);
          setDeliveryCoverageUnavailable(true);
          onDeliveryCoverageUnavailableChange(true);
          const isSelectedDelivery = mainOptionDelivery?.tipoEntrega === DeliveryType.RECEBER_EM_CASA;
          if (!preserveSelection || isSelectedDelivery)
            setMainOptionDelivery(null);
        } else {
          showAlert({
            message: errorMessage,
            hideCloseButton: true,
          });
        }
      } finally {
        setLoading(false);
        showOverlay(false);
      }
    }
  }

  function clearOptions(preserveSelection = false) {
    if (!preserveSelection) {
      setStoreForDelivery(null);
      setMainOptionDelivery(null);
    }
  }

  function getProductsInCart(updatedCart) {
    if (updatedCart) {
      return updatedCart.map(item => {
        return {
          productId: item.id,
          amount: item.quantidade
        }
      });
    } else {
      return cart.map(item => {
        return {
          productId: item.id,
          amount: item.quantidade
        }
      });
    }
  }

  function onClickPickupStore(e) {
    e.preventDefault();
    e.stopPropagation();
    showPickupModal();
  }

  function onClickChangeAddress(e) {
    e.stopPropagation();
    showSelectedAddressModal();
  }

  const isExpressaSelected = mainOptionDelivery?.tipoEntrega === DeliveryType.RECEBER_EM_CASA && deliverySubtype === DeliverySubtype.EXPRESSA;
  const isNormalSelected = mainOptionDelivery?.tipoEntrega === DeliveryType.RECEBER_EM_CASA && !deliverySubtype;
  const isRetiradaSelected = mainOptionDelivery?.tipoEntrega === DeliveryType.RETIRAR_NA_LOJA;

  const hasSelection = !!mainOptionDelivery;
  const isExpressaMuted = hasSelection && !isExpressaSelected;
  const isNormalMuted = hasSelection && !isNormalSelected;
  const isRetiradaMuted = hasSelection && !isRetiradaSelected;
  const isDeliveryUnavailable = deliveryUnavailable || deliveryCoverageUnavailable;
  const unavailableMessage = deliveryUnavailableMessage;
  const canChangeUnavailableAddress = canRegisterAddress || deliveryCoverageUnavailable;

  return (
    <Container>
      <div className="title-row">
        <h2>Escolha uma opção de entrega</h2>
      </div>
      {loading && (
        <LoaderAsync message="Carregando mais opções de entrega..." />
      )}
      <Options>
        <div className="list">
          {isDeliveryUnavailable && (
            <Card disabled={!canChangeUnavailableAddress}>
              <div className="header">
                <div className="line">
                  <IconBadge muted={!canChangeUnavailableAddress}>
                    <LocalShipping style={{ fontSize: '22px' }} />
                  </IconBadge>
                  <div className="content">
                    <p className="description">Entrega</p>
                    <div className="info-delivery">
                      <p className="time">
                        {unavailableMessage}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {canChangeUnavailableAddress && (
                <>
                  <Divider />
                  <button type="button" className="choose-address-btn" onClick={onClickChangeAddress}>
                    {canRegisterAddress ? <FiPlus size={14} /> : <FiEdit2 size={14} />}
                    {canRegisterAddress ? "Cadastrar endereço" : "Escolher outro endereço"}
                  </button>
                </>
              )}
            </Card>
          )}
          {storeForDelivery && !storeForDelivery.freteConfiguradoVendaAssistida && storeForDelivery?.local_entrega && storeForDelivery?.local_entrega.permite_entrega_expressa && storeForDelivery?.lojaHorarioExpresso && (
            <Card selected={isExpressaSelected} muted={isExpressaMuted} onClick={() => setMainOptionDelivery(storeForDelivery, DeliverySubtype.EXPRESSA)} onBlur={() => setMainOptionDelivery(storeForDelivery, DeliverySubtype.EXPRESSA)}>
              <div className="header">
                <div className="line">
                  <IconBadge muted={isExpressaMuted}>
                    <Speed style={{ fontSize: '22px' }} />
                  </IconBadge>

                  <div className="content">
                    <p className="description">Entrega Expressa</p>
                    <div className="info-delivery">
                      <p className="time">{getReceiptTime(storeForDelivery.lojaHorarioExpresso, DeliveryType.RECEBER_EM_CASA)}</p>
                      <p className="price">
                        {freteGratisExpressa ? (
                          <>
                            <span style={{ textDecoration: "line-through" }}>
                              {formatPrice(parseFloat(storeForDelivery.local_entrega.custo_entrega_expressa * deliveryMultiplicationFactor))}
                            </span>{" "}
                            Grátis
                          </>
                        ) : (
                          formatPrice(parseFloat(storeForDelivery.local_entrega.custo_entrega_expressa * deliveryMultiplicationFactor))
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <Radio id={`${DeliveryType.RECEBER_EM_CASA}-${DeliverySubtype.EXPRESSA}`} value={`${DeliveryType.RECEBER_EM_CASA}-${DeliverySubtype.EXPRESSA}`} name={`deliveryStore-${DeliveryType.RECEBER_EM_CASA}-${DeliverySubtype.EXPRESSA}`} checked={isExpressaSelected} onChange={() => setMainOptionDelivery(storeForDelivery, DeliverySubtype.EXPRESSA)} onBlur={() => setMainOptionDelivery(storeForDelivery, DeliverySubtype.EXPRESSA)} />
              </div>
            </Card>
          )}
          {storeForDelivery && (
            <Card selected={isNormalSelected} muted={isNormalMuted} onClick={() => setMainOptionDelivery(storeForDelivery)} onBlur={() => setMainOptionDelivery(storeForDelivery)}>
              <div className="header">
                <div className="line">
                  <IconBadge muted={isNormalMuted}>
                    <LocalShipping style={{ fontSize: '22px' }} />
                  </IconBadge>
                  <div className="content">
                    <p className="description">Entrega Normal</p>
                    <div className="info-delivery">
                      <p className="time">{getReceiptTime(storeForDelivery.lojaHorario, DeliveryType.RECEBER_EM_CASA)}</p>
                      <p className="price">
                        {freteGratisNormal ? (
                          <>
                            <span style={{ textDecoration: "line-through" }}>
                              {formatPrice(parseFloat(storeForDelivery.freteConfiguradoVendaAssistida
                                ? storeForDelivery.valorFreteVendaAssistida
                                : (storeForDelivery?.local_entrega?.custo_entrega ?? storeForDelivery?.custo_entrega) * deliveryMultiplicationFactor))}
                            </span>{" "}
                            Grátis
                          </>
                        ) : (
                          formatPrice(parseFloat(storeForDelivery.freteConfiguradoVendaAssistida
                            ? storeForDelivery.valorFreteVendaAssistida
                            : (storeForDelivery?.local_entrega?.custo_entrega ?? storeForDelivery?.custo_entrega) * deliveryMultiplicationFactor))
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <Radio id={DeliveryType.RECEBER_EM_CASA} value={DeliveryType.RECEBER_EM_CASA} name={`deliveryStore-${DeliveryType.RECEBER_EM_CASA}`} checked={isNormalSelected} onChange={() => setMainOptionDelivery(storeForDelivery)} onBlur={() => setMainOptionDelivery(storeForDelivery)} />
              </div>
            </Card>
          )}
          <Card selected={isRetiradaSelected} muted={isRetiradaMuted} onClick={onClickPickupStore} onBlur={onClickPickupStore}>
            <div className="header">
              <div className="line">
                <IconBadge muted={isRetiradaMuted}>
                  <Storefront style={{ fontSize: '22px' }} />
                </IconBadge>
                <div className="content">
                  <p className="description">Retirada em Loja</p>
                  <div className="info-delivery">
                    <p className="price price-free">Grátis</p>
                  </div>
                </div>
              </div>
              <Radio id={DeliveryType.RETIRAR_NA_LOJA} value={DeliveryType.RETIRAR_NA_LOJA} name="deliveryStore" checked={isRetiradaSelected} onChange={onClickPickupStore} onBlur={onClickPickupStore} />
            </div>
          </Card>
        </div>
        {/* Detalhe da opção escolhida fora dos cartões: os três mantêm a mesma largura. */}
        {(isExpressaSelected || isNormalSelected) && (
          <DetalheEntrega>
            <SelectedAddress loading={loadingAddresses} address={mainAddress} showSelectedAddressModal={onClickChangeAddress} />
          </DetalheEntrega>
        )}
        {isRetiradaSelected && (
          <DetalheEntrega>
            <StoreCard
              embedded
              name="delivery-option-picker"
              store={{
                storeId: mainOptionDelivery.id,
                storeName: mainOptionDelivery.nome_fantasia,
                address: {
                  street: mainOptionDelivery.endereco,
                  number: mainOptionDelivery.endereco_numero,
                  neighborhood: mainOptionDelivery.bairro,
                  city: mainOptionDelivery.cidade,
                  uf: mainOptionDelivery.uf || mainOptionDelivery.estado,
                },
                distance: mainOptionDelivery.distancia,
                operationTime: mainOptionDelivery.lojaHorario,
                cnpj: mainOptionDelivery.cnpj,
                pharmacistName: mainOptionDelivery.farmaceutico_nome,
                pharmacistRegistration: mainOptionDelivery.farmaceutico_inscricao,
                afe: mainOptionDelivery.caminho_afe,
              }}
              responsibleWithdrawal={responsibleWithdrawal ? {
                withdrawalByThird: responsibleWithdrawal?.retirada_terceiro,
                fullName: responsibleWithdrawal?.nome_completo,
                cpf: responsibleWithdrawal?.cpf,
                phone: responsibleWithdrawal?.telefone_contato,
              } : null}
              selectedStore={true}
              showIcon={true}
            />
            <button className="choose-address-btn" onClick={onClickPickupStore}> <FiEdit2 size={14} /> Escolher outra loja</button>
          </DetalheEntrega>
        )}
        {!isDeliveryUnavailable && !mainOptionDelivery && (
          <p className="delivery-hint">Escolha uma opção de entrega para continuar.</p>
        )}
      </Options>
    </Container >
  );
}
